import { m } from 'framer-motion';
import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';

import { RouterLink } from 'src/routes/components';
import { paths, newSearchParams } from 'src/routes/paths';

import { CONFIG } from 'src/global-config';
import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export function NavUpgrade({ sx, ...other }) {
  const { user } = useAuthContext();

  const { t, currentLang } = useTranslate();

  return (
    <Box
      sx={[{ px: 2, py: 5, textAlign: 'center' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}
    >
      <Box className="stack" sx={{ alignItems: 'center' }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar src={user?.photoURL} alt={user?.displayName} sx={{ width: 48, height: 48 }} />

          <Label
            color="success"
            variant="filled"
            sx={{
              top: -6,
              px: 0.5,
              left: 40,
              height: 20,
              position: 'absolute',
              borderBottomLeftRadius: 2,
            }}
          >
            {t('Free')}
          </Label>
        </Box>

        <Box sx={{ mb: 2, mt: 1.5, width: 1 }}>
          <Box
            className="noWrap"
            sx={{ typography: 'subtitle2', color: 'var(--layout-nav-text-primary-color)' }}
          >
            {user?.displayName}
          </Box>

          <Box
            className="noWrap"
            sx={{ typography: 'body2', color: 'var(--layout-nav-text-disabled-color)' }}
          >
            {user?.email}
          </Box>
        </Box>

        <Button
          variant="contained"
          component={RouterLink}
          href={`${paths.user.account}${newSearchParams({ country: currentLang.countryName, tab: 'subscription', plan: 'pro' })}`}
        >
          {t('Upgrade to Pro')}
        </Button>
      </Box>
    </Box>
  );
}

export function UpgradeBlock({ sx, ...other }) {
  const { user } = useAuthContext();

  const { t, currentLang } = useTranslate();

  return (
    user?.plan !== 'premium' && (
      <Box sx={[{ p: 3 }, ...(Array.isArray(sx) ? sx : [sx])]} {...other}>
        <Box
          sx={[
            (theme) => ({
              ...theme.mixins.bgGradient({
                images: [
                  `linear-gradient(315deg, ${varAlpha(theme.vars.palette.error.lightChannel, 0.92)}, ${varAlpha(theme.vars.palette.secondary.darkChannel, 0.92)})`,
                  `url(${CONFIG.assetsDir}/assets/background/background-7.webp)`,
                ],
              }),
              px: 3,
              py: 2.5,
              borderRadius: 2,
              position: 'relative',
            }),
          ]}
        >
          <Box
            sx={(theme) => ({
              top: 0,
              left: 0,
              width: 1,
              height: 1,
              borderRadius: 2,
              position: 'absolute',
              border: `solid 3px ${varAlpha(theme.vars.palette.common.whiteChannel, 0.16)}`,
            })}
          />

          <Box
            component={m.img}
            animate={{ y: [64, 0, 64] }}
            transition={{
              duration: 6,
              ease: 'linear',
              repeat: Infinity,
              repeatDelay: 0,
            }}
            alt="Small Rocket"
            src={`${CONFIG.assetsDir}/assets/illustrations/illustration-rocket-small.webp`}
            sx={{
              right: 0,
              width: 100,
              height: 100,
              position: 'absolute',
            }}
          />

          <Box className="stack" sx={{ gap: 1, position: 'relative', alignItems: 'flex-start' }}>
            <Box
              sx={(theme) => ({
                ...theme.mixins.textGradient(
                  `135deg, ${theme.vars.palette.warning.main}, ${theme.vars.palette.warning.lighter}`
                ),
              })}
            >
              <Box sx={{ typography: 'h5' }}>{t('Boost your business with')}</Box>

              <Box sx={{ typography: 'subtitle1' }}>{t(`${CONFIG.appName} ads`)}</Box>
            </Box>

            <Box sx={{ color: 'common.white', typography: 'body2', maxWidth: 200 }}>
              {t('convert viewers into customers')}.
            </Box>

            <Button
              color="secondary"
              variant="contained"
              component={RouterLink}
              href={`${paths.user.account}${newSearchParams({ country: currentLang.countryName, tab: 'subscription', plan: 'premium' })}`}
              sx={{ mt: 0.5 }}
            >
              {t('Switch to premium')}

              <Iconify icon="fluent-emoji:sparkles" width={24} sx={{ ml: 1 }} />
            </Button>
          </Box>
        </Box>
      </Box>
    )
  );
}
